import { useCallback, useEffect, useRef, useState } from "react";

import WorkerDetailedProfileEditor from "./WorkerDetailedProfileEditor.jsx";
import "./workerDetailedProfile.css";

const DIALOG_TITLE_ID = "wx-wdp-dialog-title";

/**
 * P146: the Detailed Worker Profile editor, rendered as a dialog over
 * whatever the operator was already looking at.
 *
 * Deliberately a dialog and not a route: every entry point (the Agent
 * applicant drawer, the Worker Application Inbox drawer) is already a
 * mid-task context showing this exact worker, and sending the operator to a
 * separate top-level page mid-task reads as a wrong turn rather than an
 * answer - the same reason P145's AgentWorkersDialog exists. Closing returns
 * to precisely the drawer and scroll position they came from.
 *
 * Sits at z-index 90 (see .wx-wdp-dialog-root), above the shared DetailDrawer
 * that owns the layer below it, so it can open from inside an already-open
 * drawer.
 */
export default function WorkerDetailedProfileDialog({ open, applicationId, onClose, onSaved }) {
  const dialogRef = useRef(null);
  // P157-R4: the dialog owns the close POLICY (Escape, backdrop, the X, the
  // editor's own ปิด button) because all four are its own affordances; the
  // editor owns what "unsaved" means and renders the question. Every route out
  // of here funnels through requestClose so none of them can bypass the guard.
  const [dirty, setDirty] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const requestClose = useCallback(() => {
    if (dirty) {
      setConfirming(true);
      return;
    }
    onClose?.();
  }, [dirty, onClose]);

  // A fresh open must never inherit the last session's answer.
  useEffect(() => {
    if (!open) {
      setDirty(false);
      setConfirming(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        requestClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, requestClose]);

  if (!open || !applicationId) {
    return null;
  }

  return (
    <div
      className="wx-wdp-dialog-root"
      // mousedown, not click: a click that STARTS inside the dialog and ends
      // outside it (a drag while selecting text in a textarea) must not close
      // an editor holding unsaved edits.
      onMouseDown={(event) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target)) {
          requestClose();
        }
      }}
    >
      <div className="wx-wdp-dialog-backdrop" aria-hidden="true" />
      <div
        ref={dialogRef}
        className="wx-wdp-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={DIALOG_TITLE_ID}
      >
        {/* P157-R6: the close button moved INTO the editor's header card, beside
            the completeness readout where the target composition puts it. A
            floating X over a transparent dialog surface had nothing to sit on. */}
        <div className="wx-wdp-dialog-body">
          <WorkerDetailedProfileEditor
            applicationId={applicationId}
            titleId={DIALOG_TITLE_ID}
            onSaved={onSaved}
            onRequestClose={requestClose}
            onDirtyChange={setDirty}
            confirmingClose={confirming}
            onCancelClose={() => setConfirming(false)}
            onDiscardClose={() => {
              setConfirming(false);
              onClose?.();
            }}
          />
        </div>
      </div>
    </div>
  );
}
